import { getConfig, getBaseUrl, getFileUrl, getFileApiKey, getCampusId, getUserEmail } from "@/controllers/BaseController"
import axios from "@/controllers/axiosWrapper"
import moment from "moment"

const REMOTE_WORK_TAG_KEY = "remote-work-file"

// Load SQL.js from CDN
let SQL = null
const loadSqlJs = async () => {
  if (!SQL) {
    // Dynamically load SQL.js from CDN
    if (!window.initSqlJs) {
      await new Promise((resolve, reject) => {
        const script = document.createElement("script")
        script.src = "https://cdnjs.cloudflare.com/ajax/libs/sql.js/1.8.0/sql-wasm.js"
        script.onload = resolve
        script.onerror = reject
        document.head.appendChild(script)
      })
    }

    SQL = await window.initSqlJs({
      locateFile: file => `https://cdnjs.cloudflare.com/ajax/libs/sql.js/1.8.0/${file}`
    })
  }
  return SQL
}

/**
 * Initialize Remote Work SQLite file for a campus
 */
export const initializeRemoteWorkFile = async campusId => {
  try {
    const sqliteBlob = await createEmptySQLiteFile()
    const fileId = await uploadRemoteWorkFile(sqliteBlob)
    await linkRemoteWorkFileToCampus(campusId, fileId)
    return fileId
  } catch (error) {
    console.error("Error initializing remote work file:", error)
    throw error
  }
}

/**
 * Upload SQLite file to File Service
 */
const uploadRemoteWorkFile = async blob => {
  const formData = new FormData()
  formData.append("file", blob, "remote-work.db")
  formData.append("type", "sqlite")

  const config = await getConfig()
  const fileUrl = await getFileUrl()
  const fileApiKey = await getFileApiKey()

  config.url = `${fileUrl}single`
  config.method = "POST"
  config.data = formData
  config.headers["Content-Type"] = "multipart/form-data"
  config.headers["x-ps-api-key"] = fileApiKey

  const response = await axios(config)
  return response.data.id
}

/**
 * Delete file from File Service
 */
const deleteRemoteWorkFile = async fileId => {
  try {
    const config = await getConfig()
    const fileUrl = await getFileUrl()
    const fileApiKey = await getFileApiKey()

    config.url = `${fileUrl}${fileId}`
    config.method = "DELETE"
    config.headers["Content-Type"] = "application/json" // This is the key!
    config.headers["x-ps-api-key"] = fileApiKey
    config.data = "" // Empty body

    await axios(config)
    return true
  } catch (error) {
    console.error("Error deleting remote work file:", error)
    throw error
  }
}

/**
 * Link Remote Work file to campus via tag (POST creates or updates)
 */
const linkRemoteWorkFileToCampus = async (campusId, fileId) => {
  const config = await getConfig()
  config.url = `${await getBaseUrl()}campuses/${campusId}/tags`
  config.method = "POST"
  config.data = {
    key: REMOTE_WORK_TAG_KEY,
    value: fileId
  }

  const response = await axios(config)
  return response.data
}

/**
 * Get Remote Work file ID from campus tags
 * FIXED: Tags is an object { key, value }, not an array
 */
export const getRemoteWorkFileId = async campusId => {
  try {
    const config = await getConfig()
    config.url = `${await getBaseUrl()}campuses/${campusId}`

    const response = await axios(config)
    const campus = response.data

    // Tags is an OBJECT with { key, value } structure
    if (campus.tags && campus.tags.key === REMOTE_WORK_TAG_KEY) {
      return campus.tags.value
    }

    throw new Error("Remote Work not configured for this campus")
  } catch (error) {
    console.error("Error getting remote work file ID:", error)
    throw error
  }
}

/**
 * Fetch SQLite file
 */
export const fetchRemoteWorkFile = async fileId => {
  try {
    const config = await getConfig()
    const fileUrl = await getFileUrl()

    config.url = `${fileUrl}${fileId}`
    config.headers["x-ps-api-key"] = await getFileApiKey()
    config.responseType = "blob"

    const response = await axios(config)
    return response.data
  } catch (error) {
    console.error("Error fetching remote work file:", error)
    throw error
  }
}

/**
 * Update SQLite file (Delete old + Upload new)
 */
export const updateRemoteWorkFile = async (fileId, blob) => {
  try {
    // Step 1: Delete the old file
    await deleteRemoteWorkFile(fileId)

    // Step 2: Upload the new file
    const newFileId = await uploadRemoteWorkFile(blob)

    // Step 3: Update campus tags with new file ID
    const campusId = await getCampusId()
    await linkRemoteWorkFileToCampus(campusId, newFileId)

    return { id: newFileId }
  } catch (error) {
    console.error("Error updating remote work file:", error)
    throw error
  }
}

/**
 * Create empty SQLite database structure
 */
const createEmptySQLiteFile = async () => {
  const SqlJs = await loadSqlJs()
  const db = new SqlJs.Database()

  db.run(`
    CREATE TABLE remote_work_records (
      id INTEGER PRIMARY KEY AUTOINCREMENT,
      employee_email TEXT NOT NULL,
      work_date TEXT NOT NULL,
      start_time TEXT NOT NULL,
      end_time TEXT NOT NULL,
      break_duration TEXT,
      note TEXT,
      last_updated_at TEXT NOT NULL,
      UNIQUE(employee_email, work_date)
    )
  `)

  const data = db.export()
  db.close()

  return new Blob([data], { type: "application/x-sqlite3" })
}

/**
 * Read record from SQLite for today (current user)
 */
export const getTodayRemoteWorkRecord = async () => {
  try {
    const campusId = await getCampusId()
    const fileId = await getRemoteWorkFileId(campusId)
    const blob = await fetchRemoteWorkFile(fileId)

    const arrayBuffer = await blob.arrayBuffer()
    const SqlJs = await loadSqlJs()
    const db = new SqlJs.Database(new Uint8Array(arrayBuffer))

    const userEmail = await getUserEmail()
    const today = moment().format("YYYY-MM-DD")

    const stmt = db.prepare("SELECT * FROM remote_work_records WHERE employee_email = ? AND work_date = ?")
    stmt.bind([userEmail, today])

    let record = null
    if (stmt.step()) {
      const row = stmt.getAsObject()
      record = {
        startTime: row.start_time,
        endTime: row.end_time,
        breakDuration: row.break_duration,
        note: row.note
      }
    }

    stmt.free()
    db.close()

    return record
  } catch (error) {
    console.error("Error reading remote work record:", error)
    return null
  }
}

/**
 * Get all records for today (ADMIN ONLY)
 */
export const getTodayRemoteWorkRecords = async () => {
  try {
    const campusId = await getCampusId()
    const fileId = await getRemoteWorkFileId(campusId)
    const blob = await fetchRemoteWorkFile(fileId)

    const arrayBuffer = await blob.arrayBuffer()
    const SqlJs = await loadSqlJs()
    const db = new SqlJs.Database(new Uint8Array(arrayBuffer))

    const today = moment().format("YYYY-MM-DD")

    const stmt = db.prepare("SELECT * FROM remote_work_records WHERE work_date = ? ORDER BY last_updated_at DESC")
    stmt.bind([today])

    const records = []
    while (stmt.step()) {
      records.push(stmt.getAsObject())
    }

    stmt.free()
    db.close()

    return records
  } catch (error) {
    console.error("Error reading today records:", error)
    return []
  }
}

/**
 * Get all records from last N days (ADMIN ONLY)
 */
export const getAllRemoteWorkRecords = async (days = 30) => {
  try {
    const campusId = await getCampusId()
    const fileId = await getRemoteWorkFileId(campusId)
    const blob = await fetchRemoteWorkFile(fileId)

    const arrayBuffer = await blob.arrayBuffer()
    const SqlJs = await loadSqlJs()
    const db = new SqlJs.Database(new Uint8Array(arrayBuffer))

    const startDate = moment().subtract(days, "days").format("YYYY-MM-DD")

    const stmt = db.prepare("SELECT * FROM remote_work_records WHERE work_date >= ? ORDER BY work_date DESC, last_updated_at DESC")
    stmt.bind([startDate])

    const records = []
    while (stmt.step()) {
      records.push(stmt.getAsObject())
    }

    stmt.free()
    db.close()

    return records
  } catch (error) {
    console.error("Error reading all records:", error)
    return []
  }
}

/**
 * Save/Update Remote Work record
 */
export const saveRemoteWorkRecord = async (startTime, endTime, breakDuration, note = "") => {
  try {
    const campusId = await getCampusId()
    const fileId = await getRemoteWorkFileId(campusId)
    const blob = await fetchRemoteWorkFile(fileId)

    const arrayBuffer = await blob.arrayBuffer()
    const SqlJs = await loadSqlJs()
    const db = new SqlJs.Database(new Uint8Array(arrayBuffer))

    const userEmail = await getUserEmail()
    const today = moment().format("YYYY-MM-DD")
    const timestamp = moment().toISOString()

    // Check if record exists
    const checkStmt = db.prepare("SELECT id FROM remote_work_records WHERE employee_email = ? AND work_date = ?")
    checkStmt.bind([userEmail, today])
    const exists = checkStmt.step()
    checkStmt.free()

    if (exists) {
      db.run(
        `UPDATE remote_work_records 
         SET start_time = ?, end_time = ?, break_duration = ?, note = ?, last_updated_at = ?
         WHERE employee_email = ? AND work_date = ?`,
        [startTime, endTime, breakDuration, note, timestamp, userEmail, today]
      )
    } else {
      db.run(
        `INSERT INTO remote_work_records 
         (employee_email, work_date, start_time, end_time, break_duration, note, last_updated_at)
         VALUES (?, ?, ?, ?, ?, ?, ?)`,
        [userEmail, today, startTime, endTime, breakDuration, note, timestamp]
      )
    }

    const data = db.export()
    const updatedBlob = new Blob([data], { type: "application/x-sqlite3" })

    db.close()

    await updateRemoteWorkFile(fileId, updatedBlob)

    return true
  } catch (error) {
    console.error("Error saving remote work record:", error)
    throw error
  }
}

/**
 * Validate time input
 */
export const validateTimes = (startTime, endTime, breakDuration) => {
  const start = moment(startTime, "HH:mm")
  const end = moment(endTime, "HH:mm")

  if (!start.isValid() || !end.isValid()) {
    return { valid: false, error: "Invalid time format" }
  }

  if (end.isSameOrBefore(start)) {
    return { valid: false, error: "End time must be after start time" }
  }

  const workMinutes = end.diff(start, "minutes")
  const breakMinutes = parseInt(breakDuration) || 0

  if (breakMinutes > workMinutes) {
    return { valid: false, error: "Break duration cannot exceed work time" }
  }

  return { valid: true }
}

/**
 * Check if Remote Work is configured for campus
 */
export const isRemoteWorkConfigured = async () => {
  try {
    const campusId = await getCampusId()
    await getRemoteWorkFileId(campusId)
    return true
  } catch (error) {
    return false
  }
}
